import { Component, Inject, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  FormControlStatus,
  UntypedFormGroup,
  Validators,
  FormGroup,
  FormControl,
} from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  ClientProjectDialogData,
  ClientProjectPickerDialogComponent,
} from '@app/shared/dialogs/client-project-picker-dialog/client-project-picker-dialog.component';
import { RecordToolbarService } from '@app/shared/record-toolbar/record-toolbar.service';
import { useFormErrorObservable, ObserveFormGroupErrors } from '@app/_helpers/get-error-observable';
import { findRolePerm, userProjectTaskPerm } from '@app/_helpers/userProjectFind';
import { createRxValue } from '@app/_helpers/utils';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { Logger, Project, ProjectsQuery, TasksService, UserSettings, UserSettingsQuery } from 'timeghost-api';
const log = new Logger('CreateTaskDialogComponent');
@ObserveFormGroupErrors()
@UntilDestroy()
@Component({
  selector: 'tg-create-task-dialog',
  templateUrl: './create-task-dialog.component.html',
  styleUrls: ['./create-task-dialog.component.scss'],
})
export class CreateTaskDialogComponent implements OnInit {
  taskGroup = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(3)]),
    project: new FormControl<Project>(null),
    estimation: new UntypedFormControl(null),
    assigned: new FormControl({ users: [], groups: [] }),
  });
  isLoading = createRxValue(false);
  constructor(
    private ref: MatDialogRef<CreateTaskDialogComponent>,
    private projectQuery: ProjectsQuery,
    private userSettingsQuery: UserSettingsQuery,
    private taskService: TasksService,
    private recordService: RecordToolbarService,
    private translateService: TranslateService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    private data: any
  ) {}

  ngOnInit(): void {
    this.ref.updateSize('500px');
    this.ref.addPanelClass(['mat-dialog-vanilla', 'mat-dialog-relative']);
    const project: Project = this.data.project;
    this.taskGroup.patchValue({
      project,
      name: this.data.search || '',
    });
    this.taskGroup.updateValueAndValidity();
  }
  selectedUsers$ = this.taskGroup.valueChanges.pipe(map((x) => x.assigned?.users));
  get formNameErrors() {
    if (this.taskGroup.controls.name.errors?.required) return { content: 'errors.required', args: { field: 'Name' } };
    if (this.taskGroup.controls.name.errors?.minlength)
      return { content: 'errors.minlength', args: { field: 'Name', length: 3 } };
    if (this.taskGroup.controls.name.errors?.exists) return { content: 'errors.unique', args: { field: 'Name' } };
    return null;
  }
  getFormErrorByName(controlName: string) {
    return useFormErrorObservable(this)(
      controlName,
      () => this.taskGroup.controls[controlName],
      {
        exists: (error, ctrl) => ({ content: 'errors.unique', args: { field: `Name` } }),
        required: (error, ctrl) => ({
          content: 'errors.required',
          args: { field: `Name` },
        }),
        minlength: (error, ctrl) => ({
          content: 'errors.minlength',
          args: { field: `Name` },
        }),
      },
      (key) => this.translateService.instant(key)
    );
  }
  trackId(_: number, { id }: any) {
    return id;
  }
  get defaultProject() {
    return this.projectQuery.getAll({ filterBy: (x) => x.useAsDefault })?.[0];
  }
  openProjectPicker() {
    // const project = this.taskGroup.value.project;
    // return this.dialog
    //   .open(projectscomponent, {
    //     data: <ClientProjectDialogData>{
    //       data: {
    //         selectedProject: this.group.value.project,
    //         selectedTask: this.group.value.task,
    //         closeOnRemove: true,
    //         canToggle: true,
    //         defaultProject: this.defaultProject,
    //       },
    //     },
    //   })
    //   .afterClosed()
    //   .pipe(filter((x) => !!x))
    //   .subscribe(([x, t]: [Project, Task]) => {
    //     const newProject = x ?? this.defaultProject;
    //     this.group.patchValue({
    //       project: newProject
    //     });
    //   });
  }
  readonly project$createPermission = combineLatest([
    this.userSettingsQuery.select(),
    this.taskGroup.valueChanges,
    this.isLoading.value$,
  ]).pipe(
    map(([user, { project }, isLoading]: [UserSettings, { project: Project }, boolean]) => {
      log.debug(project, this.taskGroup.status, isLoading);
      const isValid = this.taskGroup.valid;
      if (user?.workspace.users.find((x) => x.id === user.id && x.admin) && isValid) return true;
      if (isLoading || !project || !isValid) return false;
      return userProjectTaskPerm(user, project);
    })
  );

  filterProjectExclusive = (({ id, entity, type }: { id: string; entity: any; type: 'user' | 'group' }) => {
    if (!this.taskGroup.value.project?.private) return true;
    const user = this.userSettingsQuery.getValue();
    const project: Project = this.taskGroup.value.project;
    if (type === 'group') return !!project.groups?.find((x) => x.id === id);
    else if (type === 'user')
      return (
        !!project.users?.find((x) => x.id === id) ||
        !!project.groups?.find(
          (g) => !!user.workspace.groups.find((x) => x.id === g.id)?.users.find((x) => x.id === id)
        )
      );

    return true;
  }).bind(this);
  createTask() {
    if (!this.taskGroup.valid) return;
    const value = this.taskGroup.getRawValue();
    this.isLoading.value = true;
    return this.taskService
      .add({
        name: value.name,
        estimation: value.estimation
          ? (([hours, minutes]) => {
              return (hours * 60 + minutes) * 60;
            })(value.estimation.split(':').map(Number))
          : value.estimation,
        project: value.project,
        assignedToGroups: value.assigned?.groups,
        assignedToUsers: value.assigned?.users,
      })
      .toPromise()
      .then((task) => {
        this.ref.close(task);
      })
      .catch((err) => {
        this.isLoading.value = false;
        this.recordService.handleError(err);
      });
  }
}
